import { useEffect, useRef, useState } from "react";
import { GameState, Player, GameRole } from "../types";
import { buildStreamInfo } from "../utils/streamBuilder";
import { apiService } from "../services/api";

/**
 * Props for the useStreamSync hook
 */
export interface UseStreamSyncProps {
  gameState: GameState;
  gamePlayers: Player[];
  gameJudge?: Player;
  gameRoles: GameRole[];
  nominations: Record<string, string>;
  tournamentId?: number;
  clubId?: number;
  tableNumber: number;
  gameNumber?: number;
  streamBanner?: Record<string, any>;
  enabled: boolean;
  overrideGamePhase?: number | null;
  onStreamError?: (hasError: boolean) => void;
}

/**
 * Hook that automatically syncs game state to the backend via pushStream API
 *
 * Monitors game state changes and pushes updates with debouncing to prevent
 * excessive API calls. Handles errors gracefully without disrupting game flow.
 *
 * @param props - Configuration and game state data
 * @returns Object with hasError status
 */
export function useStreamSync(props: UseStreamSyncProps): { hasError: boolean } {
  const {
    gameState,
    gamePlayers,
    gameJudge,
    gameRoles,
    nominations,
    tournamentId,
    clubId,
    tableNumber,
    gameNumber,
    streamBanner,
    enabled,
    overrideGamePhase,
    onStreamError,
  } = props;

  // Store timeout reference for debouncing
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPayloadKeyRef = useRef<string | null>(null);
  const lastGamePhaseRef = useRef<number | null>(null);
  const lastRequestIdRef = useRef<number | null>(null);
  const retryCountRef = useRef<number>(0);
  
  // Track stream sync error status
  const [hasError, setHasError] = useState(false);

  const clearDebounce = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    clearDebounce();

    // Check if we have players in either gameState or gamePlayers array
    const hasPlayersInState =
      Array.isArray(gameState.players) && gameState.players.length > 0;
    const hasPlayersInProps =
      Array.isArray(gamePlayers) && gamePlayers.length > 0;
    const hasPlayers = hasPlayersInState || hasPlayersInProps;
    
    // Allow sending updates in PlayerSetup even without players (gameStage === 1)
    const isPlayerSetup = gameState.gameStage === 1;
    const shouldSkip = !enabled || (!hasPlayers && !isPlayerSetup);

    if (process.env.NODE_ENV === "development") {
      console.log("[StreamSync] State check:", {
        enabled,
        hasPlayersInState,
        hasPlayersInProps,
        hasPlayers,
        isPlayerSetup,
        shouldSkip,
        gameStage: gameState.gameStage,
        gamePhase: gameState.gamePhase,
      });
    }

    if (shouldSkip) {
      console.log("[StreamSync] Skipping - disabled or (no players and not PlayerSetup)");
      lastPayloadKeyRef.current = null;
      return;
    }

    const streamInfo = buildStreamInfo({
      gameState,
      gamePlayers,
      gameJudge,
      gameRoles,
      nominations,
      tableNumber,
      gameNumber,
      streamBanner,
      overrideGamePhase,
    });

    const payloadKey = JSON.stringify(streamInfo);
    
    // If gamePhase changed, always send update (even if other data is the same)
    const gamePhaseChanged = lastGamePhaseRef.current !== gameState.gamePhase;
    
    if (process.env.NODE_ENV === "development") {
      console.log("[StreamSync] Check:", {
        gamePhase: gameState.gamePhase,
        lastGamePhase: lastGamePhaseRef.current,
        gamePhaseChanged,
        payloadSame: lastPayloadKeyRef.current === payloadKey,
        willSend: gamePhaseChanged || lastPayloadKeyRef.current !== payloadKey,
      });
    }
    
    if (!gamePhaseChanged && lastPayloadKeyRef.current === payloadKey) {
      console.log("[StreamSync] Skipping - no changes");
      return;
    }

    // For gamePhase changes, send immediately without debounce
    // For other changes, use debounce to avoid excessive API calls
    const delay = gamePhaseChanged ? 0 : 200;

    if (process.env.NODE_ENV === "development" && gamePhaseChanged) {
      console.log("[StreamSync] Sending immediately (gamePhase changed)");
    }

    // Function to send stream update with retry mechanism
    const sendStreamUpdate = async (requestId: number) => {
      try {
        // Prepare API parameters
        const params: {
          tid?: number;
          cid?: number;
          taid: number;
          method: string;
          info: any;
        } = {
          taid: tableNumber,
          method: "PUT",
          info: streamInfo,
        };

        // Add tournament or club ID
        if (tournamentId) {
          params.tid = tournamentId;
        } else if (clubId) {
          params.cid = clubId;
        }

        // Push stream update to backend (timeout: 4000ms in apiService)
        if (process.env.NODE_ENV === "development") {
          console.log("[StreamSync] Sending request:", {
            requestId,
            gamePhase: gameState.gamePhase,
            gameStage: gameState.gameStage,
            tableNumber,
            playersCount: gamePlayers.length,
          });
        }
        
        await apiService.pushStream(params);
        console.log("[StreamSync] Request sent successfully");
        lastPayloadKeyRef.current = payloadKey;
        lastGamePhaseRef.current = gameState.gamePhase;
        retryCountRef.current = 0; // Reset retry count on success
        
        // Clear error state on success
        if (hasError) {
          setHasError(false);
          if (onStreamError) {
            onStreamError(false);
          }
        }
      } catch (error: any) {
        // Axios timeout: error.code === 'ECONNABORTED'
        // Network errors: error.message includes "Network Error" or error.code === 'ERR_NETWORK'
        const isTimeout = error?.code === 'ECONNABORTED';
        const isNetworkError = 
          error?.message?.includes("Network Error") || 
          error?.code === 'ERR_NETWORK' ||
          error?.code === 'ERR_CONNECTION_REFUSED' ||
          error?.message?.includes("ERR_CONNECTION_TIMED_OUT") ||
          error?.message?.includes("ERR_NAME_NOT_RESOLVED");
        
        if (process.env.NODE_ENV === "development") {
          console.log("[StreamSync] Error details:", {
            requestId,
            errorCode: error?.code,
            errorMessage: error?.message,
            isTimeout,
            isNetworkError,
            lastRequestId: lastRequestIdRef.current,
          });
        }
        
        // Check if this was the last request and should be retried
        if (lastRequestIdRef.current === requestId && (isTimeout || isNetworkError)) {
          // Set error state immediately on first network error
          if (!hasError) {
            setHasError(true);
            if (onStreamError) {
              onStreamError(true);
            }
          }
          
          // Limit retry attempts to prevent infinite loops
          if (retryCountRef.current < 10) {
            retryCountRef.current++;
            console.log(`[StreamSync] Retrying request ${requestId} (attempt ${retryCountRef.current})...`);
            // Retry after a short delay
            setTimeout(() => sendStreamUpdate(requestId), 1000);
            return;
          } else {
            console.error("[StreamSync] Max retry attempts reached for request", requestId);
            retryCountRef.current = 0;
          }
        }
        
        // Log error but don't throw - game should continue regardless
        if (
          error?.message?.includes("CORS")
        ) {
          // CORS error - silently ignore as it's a server configuration issue
          if (process.env.NODE_ENV === "development") {
            console.warn(
              "[StreamSync] CORS error - stream sync disabled. Game continues normally."
            );
          }
        } else {
          console.error("[StreamSync] Failed to push stream update:", error);
        }
        lastPayloadKeyRef.current = null;
      } finally {
        debounceTimeoutRef.current = null;
      }
    };

    // Debounce the stream push (200ms delay for better responsiveness, 0ms for gamePhase changes)
    debounceTimeoutRef.current = setTimeout(async () => {
      const requestId = Date.now();
      lastRequestIdRef.current = requestId;
      retryCountRef.current = 0; // Reset retry counter for new request
      await sendStreamUpdate(requestId);
    }, delay);

    // Cleanup timeout on unmount or dependency change
    return () => {
      clearDebounce();
    };
  }, [
    gameState.gameStage,
    gameState.gamePhase,
    gameState.playersLeaved,
    gameState.playersLeavingNow,
    gameState.playerFauls,
    gameState.nightCount,
    gameState.pobeda,
    gamePlayers.length,
    gameRoles.length,
    tournamentId,
    clubId,
    tableNumber,
    gameNumber,
    streamBanner,
    enabled,
    gameState,
    gamePlayers,
    gameJudge,
    gameRoles,
    nominations,
    overrideGamePhase,
    hasError,
    onStreamError,
  ]);
  
  return { hasError };
}
